import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import useSWRInfinite from "swr/infinite";
import { Empty, Spin } from "antd";
import { useAppSelector } from "@/store/hooks";
import { IProduct, ICategory, IProductsQuery } from "@/props/IProduct";
import { getProductsApi } from "@/api/product";
import MobileMenu from "@/components/Mobile/Menu/Index";
import MobileMallFilter from "@/components/Mobile/Mall/Filter";
import SortFilter from "@/components/Mall/SortFilter";
import type { LayoutMode } from "@/components/Mall/SortFilter";
import ProductItem from "@/components/Mall/ProductItem";
import Scroll from "../Common/Scroll/Index";

const PAGE_SIZE = 10;

const MobileProductList = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isMobile } = useAppSelector((state) => state.app);
  const [initLoad, setInitLoad] = useState<boolean>(false);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>("grid");
  const [filterParams, setFilterParams] = useState<
    Omit<IProductsQuery, "page" | "limit">
  >({
    priceOrder: "asc",
    shelfOrder: "",
    brandId: "",
    cid: "",
  });

  const keyword =
    typeof router.query.keyword === "string" ? router.query.keyword : "";

  const getKey = (pageIndex: number, previousPageData: any) => {
    if (
      previousPageData &&
      Array.isArray(previousPageData.list) &&
      previousPageData.list.length < PAGE_SIZE
    )
      return null; // 到底了便不再请求下一页
    return [
      pageIndex + 1,
      {
        ...filterParams,
        keyword,
        limit: PAGE_SIZE,
      },
    ];
  };

  const fetcher = async ([page, params]: [number, IProductsQuery]) => {
    const query = { ...params, page };
    const res: any = await getProductsApi(query);
    res.list.forEach((item: IProduct) => {
      item.storeName = item.storeName ? JSON.parse(item.storeName) : {};
      item.image = item.image ? JSON.parse(item.image) : {};
      item.categories.forEach((category: ICategory) => {
        category.name = JSON.parse(category.name);
      });
    });
    return res;
  };

  const { data, error, size, setSize, isValidating, isLoading } =
    useSWRInfinite(getKey, fetcher, {
      revalidateFirstPage: false,
    });

  const products: IProduct[] = useMemo(
    () => (data ? data.flatMap((page) => page.list) : []),
    [data]
  );

  const isEmpty = !data || data[0]?.list?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.list?.length < PAGE_SIZE);

  const handlePullup = useCallback(() => {
    if (!isReachingEnd && !isValidating) {
      setSize(size + 1);
    }
  }, [isReachingEnd, setSize, size, isValidating]);

  const onSortChange = (activeIndex: number, value: string) => {
    setFilterParams((prev) => ({
      ...prev,
      priceOrder: activeIndex === 0 ? value : "",
      shelfOrder: activeIndex === 1 ? value : "",
    }));
  };

  const onMobileSearch = (params: IProductsQuery) => {
    setFilterParams((prev) => ({
      ...prev,
      minPrice: params.minPrice ?? undefined,
      maxPrice: params.maxPrice ?? undefined,
      cid: params.cid || "",
      brandId: params.brandId || "",
    }));
  };

  useEffect(() => {
    if (data && !isLoading) {
      setInitLoad(true);
    }
  }, [data, isLoading]);

  if (!isMobile) return null;

  return (
    <>
      <div className="mt-2 flex w-full justify-between px-mobile lg:hidden">
        <div className="flex flex-1 flex-col">
          <SortFilter onChange={onSortChange} layoutMode={layoutMode} onLayoutChange={setLayoutMode} />

          {!initLoad || (products && products.length) ? (
            <Scroll
              wrapHeight="calc(100svh - 178px)"
              onPullup={handlePullup}
              prop={{
                data: products,
              }}
            >
              <div className="mb-6 flex w-full flex-col items-center lg:mb-20">
                <div
                  className={
                    layoutMode === "list"
                      ? "flex w-full flex-col gap-3"
                      : "grid w-full grid-cols-2 gap-mobile md:grid-cols-3"
                  }
                >
                  {products.map((product) => (
                    <ProductItem key={product.id} product={product} layout={layoutMode} showFavoriteIcon />
                  ))}
                </div>
              </div>

              <div className="mb-10 flex h-14 items-center justify-center">
                {isValidating && <Spin />}
              </div>
            </Scroll>
          ) : (
            <Empty className="mt-20" description={t("common.no_data")}></Empty>
          )}
        </div>
      </div>
      <MobileMenu />
      <MobileMallFilter onMobileSearch={onMobileSearch} />
    </>
  );
};

export default MobileProductList;
